import { Injectable } from "@angular/core";
import { MCEnvironmentsService, Store, FormisForm, SectionSchema } from "@maslul/common-services";
import { Observable } from "rxjs";
import { FormisFormStoreData } from "../../models/store/formis-form-store-data"; // ייבוא הטיפוס החדש
import { FormlyFieldConfig } from "@ngx-formly/core";

export class ProductsStoreData {
    products: Map<string, FormisFormStoreData> | undefined;
    activeProduct: string | undefined;

    constructor(initialData?: Partial<ProductsStoreData>) {
        if (initialData) {
            Object.assign(this, initialData);
            if (initialData.products && !(initialData.products instanceof Map)) {
                this.products = new Map(Object.entries(initialData.products) as [string, FormisFormStoreData][]);
            }
        }
    }
}

const PRODUCT_INITIAL_STATE: ProductsStoreData = {
    products: undefined,
    activeProduct: undefined
};

@Injectable({
    providedIn: 'root',
})
export class ProductsStoreService extends Store<ProductsStoreData> {

    constructor(protected override environmentsService: MCEnvironmentsService) {
        super('ProductsStore', PRODUCT_INITIAL_STATE, environmentsService);
    }

    setProductsMap(products: Map<string, FormisFormStoreData> | undefined) {
        this.updateState('[Products Store Service] set productsList', { products });
    }


    getProductsList(): Observable<Map<string, FormisFormStoreData> | undefined> {
        return this.select(s => s.products);
    }

    addProduct(formisFormStoreData: FormisFormStoreData, guid: string | undefined): void {
        if (!guid) { return; }
        const guidString = guid.toString();
        const currentState: ProductsStoreData = this.getSnapshot();
        const currentProducts = currentState.products;

        const newProductsMap = new Map(currentProducts);
        newProductsMap.set(guidString, formisFormStoreData);

        this.updateState('[Products Store Service] add product', { products: newProductsMap });
    }

    // #region New update methods for specific fields

    /**
     * Updates the formisForm object for a specific product.
     * @param guid The GUID of the product to update.
     * @param updatedForm The new FormisForm object.
     */
    updateProductForm(guid: string, updatedForm: FormisForm): void {
        this.updateProductData(guid, { formisForm: updatedForm });
    }

    /**
     * Updates the model object for a specific product.
     * @param guid The GUID of the product to update.
     * @param updatedModel The new model object.
     */
    updateProductModel(guid: string, updatedModel: any): void {
        this.updateProductData(guid, { formisFormModel: updatedModel });
    }

    /**
     * Updates the fields array for a specific product.
     * @param guid The GUID of the product to update.
     * @param updatedFields The new fields array.
     */
    updateProductFields(guid: string, updatedFields: Array<FormlyFieldConfig>): void {
        this.updateProductData(guid, { formisFormFields: updatedFields });
    }

    /**
 * Updates the schemas object for a specific product.
 * @param guid The GUID of the product to update.
 * @param updatedModel The new schemas object.
 */
    updateProductSchema(guid: string, schemas: SectionSchema[]): void {
        this.updateProductData(guid, { schemas: schemas });
    }

    /**
     * A private helper method to handle the actual state update logic.
     * This prevents code duplication.
     * @param guid The GUID of the product.
     * @param partialUpdate An object containing the properties to update.
     */
    private updateProductData(guid: string, partialUpdate: Partial<FormisFormStoreData>): void {
        if (!guid) {
            console.warn('Cannot update product, GUID is undefined.');
            return;
        }
        const guidString = guid.toString();
        const currentState: ProductsStoreData = this.getSnapshot();
        const currentProducts = currentState.products;

        if (!currentProducts?.has(guidString)) {
            console.warn(`Product with GUID ${guidString} not found for update.`);
            return;
        }

        const existingProduct = currentProducts.get(guidString);
        if (!existingProduct) {
            console.error(`Error: Product ${guidString} found in map but get() returned undefined.`);
            return;
        }

        // Create a new updated product object
        const updatedProduct: FormisFormStoreData = { ...existingProduct, ...partialUpdate };

        // Create a new map with the updated product
        const newProductsMap = new Map(currentProducts);
        newProductsMap.set(guidString, updatedProduct);

        // Update the state with the new map
        this.updateState(`[Products Store Service] update product data for GUID ${guidString}`, { products: newProductsMap });
    }

    // #endregion

    setActiveProduct(guid: string): void {
        this.updateState('[Products Store Service] set currentRow', { activeProduct: guid })
    }

    getActiveProduct(): Observable<string | undefined> {
        return this.select(s => s.activeProduct);
    }

    getProduct(guid: string): Observable<FormisFormStoreData | undefined> {
        return this.select(s => s.products?.get(guid));
    }

    // ... שאר הפונקציות נשארות ללא שינוי
    getNextProductGuid(): string | null {
        const currentState: ProductsStoreData = this.getSnapshot();
        const productGuids = Array.from(currentState.products?.keys() || []);
        const currentActiveGuid = currentState.activeProduct;

        if (!currentActiveGuid || productGuids.length === 0) {
            console.log('No active product or no products to navigate next.');
            return null;
        }

        const currentIndex = productGuids.indexOf(currentActiveGuid);
        if (currentIndex === -1) {
            console.warn(`Active product GUID ${currentActiveGuid} not found in product list.`);
            return null;
        }
        const nextIndex = currentIndex + 1;
        if (nextIndex < productGuids.length) {
            return productGuids[nextIndex];
        } else {
            console.log('Reached the end of the product list, no next product.');
            return null;
        }
    }

    getPreviousProductGuid(): string | null {
        const currentState: ProductsStoreData = this.getSnapshot();
        const productGuids = Array.from(currentState.products?.keys() || []);
        const currentActiveGuid = currentState.activeProduct;
        if (!currentActiveGuid || productGuids.length === 0) {
            console.log('No active product or no products to navigate previous.');
            return null;
        }
        const currentIndex = productGuids.indexOf(currentActiveGuid);
        if (currentIndex === -1) {
            console.warn(`Active product GUID ${currentActiveGuid} not found in product list.`);
            return null;
        }
        const previousIndex = currentIndex - 1;
        if (previousIndex >= 0) {
            return productGuids[previousIndex];
        } else {
            console.log('Reached the beginning of the product list, no previous product.');
            return null;
        }
    }

    getFirstGuid(): string | null {
        const currentState: ProductsStoreData = this.getSnapshot();
        const productGuids = Array.from(currentState.products?.keys() || []);
        if (productGuids.length > 0) {
            return productGuids[0];
        } else {
            return null;
        }
    }

    getLastGuid(): string | null {
        const currentState: ProductsStoreData = this.getSnapshot();
        const productGuids = Array.from(currentState.products?.keys() || []);
        if (productGuids.length > 0) {
            return productGuids[productGuids.length - 1];
        } else {
            return null;
        }
    }
}
